import React from 'react';
import { Box, Paper, Typography, Button, Chip, Tooltip, CircularProgress, alpha } from '@mui/material';
import { Save, Close, CheckCircle, EventBusy } from '@mui/icons-material';
import { AttendanceStatus } from '../../types';

// ============================================================================
// Mark-Mode floating bar.
//
// Lives at the bottom of the viewport while the user is editing a single day
// column inline on the monthly grid. Shows a live tally of the staged
// statuses, exposes the "Mark All Present" express action, and gates Save
// behind a clear count so the user knows what they're committing.
// ============================================================================

const STATUS_PILLS: { value: AttendanceStatus; short: string; label: string; color: string }[] = [
  { value: AttendanceStatus.PRESENT,  short: 'P',  label: 'Present',   color: '#22c55e' },
  { value: AttendanceStatus.ABSENT,   short: 'A',  label: 'Absent',    color: '#ef4444' },
  { value: AttendanceStatus.LATE,     short: 'L',  label: 'Late',      color: '#f59e0b' },
  { value: AttendanceStatus.HALF_DAY, short: 'H',  label: 'Half Day',  color: '#3b82f6' },
  { value: AttendanceStatus.LEAVE,    short: 'LE', label: 'Leave',     color: '#06b6d4' }
];

interface Props {
  dateLabel: string;             // e.g. "Mon, 12 May"
  counts: Record<AttendanceStatus, number>;
  totalDraftable: number;        // visible students who can be marked (excludes overlay)
  changedFromSaved: number;      // how many drafts differ from a previously-saved record
  saving: boolean;
  onMarkAllPresent: () => void;
  onCancel: () => void;
  onSave: () => void;
}

const AttendanceMarkBar: React.FC<Props> = ({
  dateLabel,
  counts,
  totalDraftable,
  changedFromSaved,
  saving,
  onMarkAllPresent,
  onCancel,
  onSave
}) => {
  const totalMarked = STATUS_PILLS.reduce((sum, pill) => sum + (counts[pill.value] || 0), 0);
  // Anything we don't show a pill for (SCHEDULED_OFF / HOLIDAY) is hidden — those
  // come from the overlay and aren't editable in Mark Mode.

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.snackbar,
        borderRadius: 3,
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 560,
        maxWidth: 'calc(100vw - 32px)',
        backgroundColor: (theme) => theme.palette.background.paper,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.common.black, 0.18)}`
      }}
    >
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Marking
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.1 }}>
          {dateLabel}
        </Typography>
      </Box>

      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider' }} />

      <Box display="flex" alignItems="center" gap={0.75} flex={1} flexWrap="wrap">
        {STATUS_PILLS.map(pill => {
          const n = counts[pill.value] || 0;
          if (n === 0) return null;
          return (
            <Chip
              key={pill.value}
              size="small"
              label={`${n} ${pill.short}`}
              sx={{
                bgcolor: alpha(pill.color, 0.12),
                color: pill.color,
                fontWeight: 700,
                border: `1px solid ${alpha(pill.color, 0.35)}`,
                height: 24
              }}
            />
          );
        })}
        {totalMarked === 0 && (
          <Typography variant="caption" color="text.secondary">
            Nothing staged yet. Click cells in the column to set statuses.
          </Typography>
        )}
        {changedFromSaved > 0 && (
          <Tooltip title={`${changedFromSaved} record(s) will overwrite values that were already saved.`}>
            <Chip
              size="small"
              icon={<EventBusy sx={{ fontSize: 14 }} />}
              label={`${changedFromSaved} overwriting`}
              color="warning"
              variant="outlined"
              sx={{ height: 24, fontWeight: 600 }}
            />
          </Tooltip>
        )}
      </Box>

      <Tooltip title="Set every visible student to Present">
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckCircle />}
            onClick={onMarkAllPresent}
            disabled={saving || totalDraftable === 0}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Mark all Present
          </Button>
        </span>
      </Tooltip>

      <Button
        size="small"
        onClick={onCancel}
        disabled={saving}
        startIcon={<Close />}
        sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        Cancel
      </Button>

      <Button
        size="small"
        variant="contained"
        onClick={onSave}
        disabled={saving || totalMarked === 0}
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2.5 }}
      >
        {saving ? 'Saving…' : `Save ${totalMarked}`}
      </Button>
    </Paper>
  );
};

export default AttendanceMarkBar;
